import { motion } from 'motion/react';

const FLAMES = [
  { left: '8%', delay: 0, scale: 1.15, height: '95%' },
  { left: '28%', delay: 0.04, scale: 1.35, height: '110%' },
  { left: '48%', delay: 0.02, scale: 1.2, height: '100%' },
  { left: '66%', delay: 0.07, scale: 1.4, height: '120%' },
  { left: '82%', delay: 0.05, scale: 1.1, height: '90%' },
];

const EMBERS = [
  { left: '22%', delay: 0.28 },
  { left: '40%', delay: 0.34 },
  { left: '58%', delay: 0.3 },
  { left: '74%', delay: 0.38 },
];

const STATIC_FLAMES = [
  { left: '10%', height: '52%' },
  { left: '28%', height: '68%' },
  { left: '46%', height: '58%' },
  { left: '62%', height: '70%' },
  { left: '78%', height: '50%' },
];

const STATIC_EMBERS = [
  { left: '24%', bottom: '22%' },
  { left: '48%', bottom: '30%' },
  { left: '68%', bottom: '20%' },
];

/** Fire overlay. Parent must use rest/dispose variants so this only appears on exit. */
export function FlameBurst({
  staticVisible = false,
}: {
  staticVisible?: boolean;
}) {
  if (staticVisible) {
    return (
      <div
        data-name="RAFFLE_FLAME_BURST_STATIC"
        className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
      >
        {STATIC_FLAMES.map((flame, index) => (
          <div
            key={index}
            className="absolute bottom-0 w-[22%] rounded-full"
            style={{
              left: flame.left,
              height: flame.height,
              background:
                'linear-gradient(to top, #FF6B3D 0%, #F5B942 55%, rgba(255,255,255,0.45) 100%)',
              filter: 'blur(0.5px)',
              transformOrigin: 'bottom center',
              opacity: 0.7,
            }}
          />
        ))}
        {STATIC_EMBERS.map((ember, index) => (
          <span
            key={`ember-${index}`}
            className="absolute h-1.5 w-1.5 rounded-full bg-[#ffb020]"
            style={{
              left: ember.left,
              bottom: ember.bottom,
              boxShadow: '0 0 6px #ff6b3d',
              opacity: 0.65,
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className="pointer-events-none absolute inset-0 z-10 overflow-visible"
      variants={{
        rest: { opacity: 0 },
        dispose: { opacity: 1, transition: { duration: 0.08 } },
      }}
    >
      {FLAMES.map((flame, index) => (
        <motion.div
          key={index}
          className="absolute bottom-0 w-[28%] rounded-full"
          style={{
            left: flame.left,
            height: flame.height,
            background:
              'radial-gradient(circle at 50% 80%, #fff6a3 0%, #ffb020 28%, #ff6b3d 52%, #ff3b00 70%, rgba(255,0,0,0) 82%)',
            filter: 'blur(1px)',
            transformOrigin: 'bottom center',
            mixBlendMode: 'screen',
          }}
          variants={{
            rest: { y: 16, scale: 0.35, opacity: 0 },
            dispose: {
              y: -72,
              scale: flame.scale * 1.25,
              opacity: [0, 1, 0.85, 0],
              transition: {
                duration: 0.8,
                delay: flame.delay,
                times: [0, 0.15, 0.55, 1],
                ease: 'easeOut',
              },
            },
          }}
        />
      ))}
      {EMBERS.map((ember, index) => (
        <motion.span
          key={`ember-${index}`}
          className="absolute bottom-2 h-2 w-2 rounded-full bg-[#ffb020]"
          style={{ left: ember.left, boxShadow: '0 0 8px #ff6b3d' }}
          variants={{
            rest: { y: 0, opacity: 0, scale: 0.4 },
            dispose: {
              y: -90,
              x: index % 2 === 0 ? -12 : 14,
              opacity: [0, 1, 0],
              scale: [0.4, 1, 0.2],
              transition: { duration: 0.75, delay: ember.delay },
            },
          }}
        />
      ))}
    </motion.div>
  );
}
